import { useState } from 'react';

import loadable from '@loadable/component';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';
import classNames from 'classnames';

import {
  BpkButtonV2,
  BUTTON_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import { withRtlSupport } from '@skyscanner/backpack-web/bpk-component-icon';
import ChevronDownIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/chevron-down';
import ChevronUpIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/chevron-up';
import BpkSectionHeader from '@skyscanner/backpack-web/bpk-component-section-header';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner/backpack-web/bpk-component-text';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import withErrorBoundary from '../ErrorBoundary';
import FooterBlurb from '../common/FooterBlurb';
import { useMBDContext } from '../common/MBD/useMBDContext';

import PriceBar from './PriceBar/PriceBar';
import Snippet from './Snippet/Snippet';

import type {
  CheapestTimeToProps,
  CheapestTimeToMonth,
  CheapestTimeToSnippetProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/CheapestTimeToProps';

import STYLES from './CheapestTimeTo.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);
const AlignedChevronDown = withRtlSupport(ChevronDownIcon);
const AlignedChevronUp = withRtlSupport(ChevronUpIcon);

const CHEVRON_STYLE = { width: 16, height: 16 };

const DEFAULT_DISPLAY_NUMBERS = 6;

const replacePlaceholders = (string: string, replacements = {}) =>
  Object.entries(replacements).reduce((replacedString, [key, value]) => {
    let s = replacedString;
    while (s.includes(key)) {
      s = s.replace(key, value as string);
    }
    return s;
  }, string);

const getPrices = (data: CheapestTimeToMonth[]) =>
  data.map((d) => d.price || 0);

const calculateMinPrice = (prices: number[]) =>
  Math.min(...prices.filter((price) => price));

const calculateMaxShownPrice = (prices: number[]) => {
  if (prices.length <= 4) return Math.ceil(Math.max(...prices));
  prices.sort((a, b) => a - b);
  const quarterI = Math.floor(prices.length / 4);
  const quartiles = [
    prices[quarterI],
    prices[2 * quarterI],
    prices[3 * quarterI],
  ];
  const outlierLimit = quartiles[2] + 2 * (quartiles[2] - quartiles[0]);
  return Math.ceil(Math.max(...prices.filter((p) => p < outlierLimit)));
};

const CheapestTimeTo = (props: CheapestTimeToProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  const [showMonthsSize, setShowMonthsSize] = useState<number>(6);
  const [showMoreMonthsHidden, setShowMoreMonthsHidden] =
    useState<boolean>(false);

  const onShowMoreMonthsClicked = () => {
    setShowMonthsSize(12);
    setShowMoreMonthsHidden(true);
  };

  if (!props.data || props.data.length === 0) {
    return null;
  }

  const {
    data,
    disclaimer = '',
    fromString,
    headerText = '',
    itemAriaLabel,
    itemCheapestAriaLabel,
    rtl,
    searchPricesText,
    showMoreMonths,
    subHeaderText = '',
  } = props;

  const prices = getPrices(data);

  if (prices.length === 0) {
    return null;
  }

  const minPrice = calculateMinPrice(prices);
  const maxPrice = calculateMaxShownPrice(prices);

  const showMoreMonthsStyleClass = showMoreMonthsHidden
    ? STYLES.expandCollapseHidden
    : STYLES.expandCollapse;
  const className = classNames(STYLES.CheapestTimeTo, {
    [STYLES.rtl]: rtl,
  });

  return (
    <div
      className={className}
      id="cheapest-time-to"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
    >
      <div className={STYLES.CheapestTimeTo__header}>
        <BpkSectionHeader title={headerText} description={subHeaderText} />
      </div>
      <div className={STYLES.main}>
        <div className={STYLES.gridContainer} id="cheapest-time-to-list">
          {data.map((month, index) => {
            const { formattedPrice, link, monthAbbreviated, monthName, price } =
              month;
            const isCheapest = minPrice === price;
            const monthRowStaStyle =
              index >= showMonthsSize ? STYLES['month-row-hide'] : '';

            return (
              <div
                className={`${STYLES['month-row']} ${monthRowStaStyle}`}
                key={`month_${monthAbbreviated}`}
              >
                <div className={STYLES['month-name']}>
                  <BpkText
                    color={TEXT_COLORS.textPrimary}
                    textStyle={TEXT_STYLES.label1}
                    className={STYLES.month}
                  >
                    {monthAbbreviated}
                  </BpkText>
                </div>
                <div className={STYLES['month-bar']}>
                  <PriceBar
                    monthIndex={index}
                    price={price}
                    maxPrice={maxPrice}
                    priceText={replacePlaceholders(fromString, {
                      '{price}': formattedPrice,
                    })}
                    url={link}
                    noPriceText={searchPricesText}
                    isCheapest={isCheapest}
                    priceAriaLabel={replacePlaceholders(
                      isCheapest ? itemCheapestAriaLabel : itemAriaLabel,
                      {
                        '@@price@@': formattedPrice,
                        '@@month@@': monthName,
                      },
                    )}
                    useV2={false}
                  />
                </div>
              </div>
            );
          })}
        </div>
        {showMonthsSize < data.length && (
          <BpkButtonV2
            onClick={onShowMoreMonthsClicked}
            data-tracking-element-id={`${rawComponentName}_cta_more`}
            data-tracking-common-params={buildMBDParam(
              `${rawComponentName}_cta`,
              {
                cta_action: 'more',
              },
            )}
            className={showMoreMonthsStyleClass}
            aria-expanded={showMoreMonthsHidden}
            aria-controls="cheapest-time-to-list"
            type={BUTTON_TYPES.link}
            implicit
          >
            <BpkText
              textStyle={TEXT_STYLES.label3}
              className={STYLES.expandCollapseText}
            >
              {showMoreMonths}
            </BpkText>
            <AlignedChevronDown
              className={STYLES.chevronArrow}
              style={CHEVRON_STYLE}
            />
          </BpkButtonV2>
        )}
      </div>
      {disclaimer && <FooterBlurb disclaimerCopy={disclaimer} />}
    </div>
  );
};

const CheapestTimeToV2 = (props: CheapestTimeToProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  const [showMonthsSize, setShowMonthsSize] = useState<number>(
    DEFAULT_DISPLAY_NUMBERS,
  );

  const onShowMoreMonthsClicked = () => {
    setShowMonthsSize(props.data.length);
  };

  const onShowLessMonthsClicked = () => {
    setShowMonthsSize(DEFAULT_DISPLAY_NUMBERS);
  };

  if (!props.data || props.data.length === 0) {
    return null;
  }

  const {
    data,
    descriptionText = '',
    disclaimer = '',
    fromString,
    headerText = '',
    infoSnippets,
    itemAriaLabel,
    itemCheapestAriaLabel,
    rtl,
    searchPricesText,
    showLessMonths = '',
    showMoreMonths,
    subHeaderText = '',
  } = props;

  const prices = getPrices(data);

  if (prices.length === 0) {
    return null;
  }
  if (infoSnippets?.length === 0) {
    return null;
  }
  const cheapestPriceSnippet: CheapestTimeToSnippetProps | undefined =
    infoSnippets?.find((x) => x.id === 'cheapestMonth');

  const mostExpensiveSnippet: CheapestTimeToSnippetProps | undefined =
    infoSnippets?.find((x) => x.id === 'mostExpensiveMonth');

  const minPrice = calculateMinPrice(prices);
  const maxPrice = calculateMaxShownPrice(prices);

  const showMoreMonthsButton =
    props.data.length > DEFAULT_DISPLAY_NUMBERS &&
    showMonthsSize <= DEFAULT_DISPLAY_NUMBERS;

  const showLessMonthsButton =
    props.data.length > DEFAULT_DISPLAY_NUMBERS &&
    showMonthsSize > DEFAULT_DISPLAY_NUMBERS;

  const showMoreMonthsStyleClass = showMoreMonthsButton
    ? STYLES.expandCollapse
    : STYLES.expandCollapseHidden;

  const showLessMonthsStyleClass = showLessMonthsButton
    ? STYLES.expandCollapse
    : STYLES.expandCollapseHidden;

  const className = classNames(STYLES.CheapestTimeTo, {
    [STYLES.rtl]: rtl,
  });

  const withDescriptionStyle = !!descriptionText;

  return (
    <div
      className={className}
      id="cheapest-time-to-v2"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName, {
        version: 'v2',
      })}
    >
      <div className={STYLES.CheapestTimeTo__header}>
        <BpkSectionHeader title={headerText} description={subHeaderText} />
      </div>
      <div
        className={
          withDescriptionStyle ? STYLES.mainV2WithDescription : STYLES.mainV2
        }
      >
        <div
          className={STYLES.gridColumnChart}
          id="cheapest-time-to-list-changed"
        >
          {data.map((month, index) => {
            const { formattedPrice, link, monthAbbreviated, monthName, price } =
              month;
            const isCheapest = minPrice === price;
            const monthRowStaStyle =
              index >= showMonthsSize ? STYLES['month-row-hide'] : '';

            return (
              <div
                className={`${STYLES['month-row']} ${monthRowStaStyle}`}
                key={`month_${monthAbbreviated}`}
              >
                <div className={STYLES['month-name']}>
                  <BpkText
                    color={TEXT_COLORS.textPrimary}
                    textStyle={TEXT_STYLES.label1}
                    className={STYLES.month}
                  >
                    {monthAbbreviated}
                  </BpkText>
                </div>
                <div className={STYLES['month-bar']}>
                  <PriceBar
                    monthIndex={index}
                    price={price}
                    maxPrice={maxPrice}
                    priceText={replacePlaceholders(fromString, {
                      '{price}': formattedPrice,
                    })}
                    url={link}
                    noPriceText={searchPricesText}
                    isCheapest={isCheapest}
                    priceAriaLabel={replacePlaceholders(
                      isCheapest ? itemCheapestAriaLabel : itemAriaLabel,
                      {
                        '@@price@@': formattedPrice,
                        '@@month@@': monthName,
                      },
                    )}
                    useV2
                  />
                </div>
              </div>
            );
          })}

          {showMoreMonthsButton && (
            <BpkButtonV2
              onClick={onShowMoreMonthsClicked}
              data-tracking-element-id={`${rawComponentName}_cta_more`}
              data-tracking-common-params={buildMBDParam(
                `${rawComponentName}_cta`,
                {
                  version: 'v2',
                  cta_action: 'more',
                },
              )}
              className={showMoreMonthsStyleClass}
              aria-expanded={showMoreMonthsButton}
              aria-controls="cheapest-time-to-list"
              type={BUTTON_TYPES.link}
              implicit
            >
              <BpkText
                color={TEXT_COLORS.textLink}
                textStyle={TEXT_STYLES.label3}
                className={STYLES.expandCollapseText}
              >
                {showMoreMonths}
              </BpkText>
              <ChevronDownIcon
                className={STYLES.chevronArrow}
                style={CHEVRON_STYLE}
              />
            </BpkButtonV2>
          )}
          {showLessMonths && showLessMonthsButton && (
            <BpkButtonV2
              onClick={onShowLessMonthsClicked}
              data-tracking-element-id={`${rawComponentName}_cta_less`}
              data-tracking-common-params={buildMBDParam(
                `${rawComponentName}_cta`,
                {
                  version: 'v2',
                  cta_action: 'less',
                },
              )}
              className={showLessMonthsStyleClass}
              aria-expanded={showLessMonthsButton}
              aria-controls="cheapest-time-to-list"
              type={BUTTON_TYPES.link}
              implicit
            >
              <BpkText
                textStyle={TEXT_STYLES.label3}
                className={STYLES.expandCollapseText}
              >
                {showLessMonths}
              </BpkText>
              <AlignedChevronUp
                className={STYLES.chevronArrow}
                style={CHEVRON_STYLE}
              />
            </BpkButtonV2>
          )}
        </div>
        {!withDescriptionStyle ? (
          <>
            <div className={STYLES.gridColumnSnippetFact1}>
              {cheapestPriceSnippet && (
                <Snippet
                  {...cheapestPriceSnippet}
                  mbdComponentName={`${rawComponentName}_cheap`}
                />
              )}
            </div>
            <div className={STYLES.gridColumnSnippetFact2}>
              {mostExpensiveSnippet && (
                <Snippet
                  {...mostExpensiveSnippet}
                  mbdComponentName={`${rawComponentName}_expensive`}
                />
              )}
            </div>
          </>
        ) : (
          <>
            <BpkText
              textStyle={TEXT_STYLES.bodyDefault}
              className={STYLES.gridColumnDescription}
            >
              {descriptionText}
            </BpkText>
            <div
              className={
                (props.infoSnippets?.length ?? 0) <= 2
                  ? STYLES.gridColumnSnippetBlock1Row
                  : STYLES.gridColumnSnippetBlock2Row
              }
            >
              {infoSnippets?.map((snippet, index) => (
                <div
                  className={STYLES[`gridColumnSnippetItem${index + 1}`]}
                  key={snippet.id}
                >
                  <Snippet
                    key={snippet.id}
                    index={index}
                    {...snippet}
                    mbdComponentName={`${rawComponentName}_snippet`}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {disclaimer && <FooterBlurb disclaimerCopy={disclaimer} />}
    </div>
  );
};

const CheapestTimeToWithErrorV2Boundary = withErrorBoundary(
  CheapestTimeToV2,
  'cheapest-time-to-component-v2',
);

const CheapestTimeToWithErrorBoundary = withErrorBoundary(
  CheapestTimeTo,
  'cheapest-time-to-component',
);

export default (props: CheapestTimeToProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.CHEAPEST_TIME_TO,
      })
    }
  >
    {props.useCheapestTimeV2Component ? (
      <CheapestTimeToWithErrorV2Boundary {...props} />
    ) : (
      <CheapestTimeToWithErrorBoundary {...props} />
    )}
  </IntersectionObserverWrapper>
);
